<template>
  <c-trigger
    :popup-translate="props.popupTranslate"
    :click-to-close="false"
    @popup-visible-change="handleVisibleChange"
  >
    <div
      v-if="!props.selection"
      class="custom-edit-div"
      :class="{
        'custom-edit-div-focus': inputAttr.focus,
        'custom-edit-div-disabled': props.disabled,
        'custom-edit-div-readonly': props.readonly,
        'custom-edit-div-error': props.error,
      }"
    >
      <div
        ref="editDivRef"
        class="custom-edit-div-wrapper"
        :spellcheck="false"
        :contenteditable="contenteditable"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="
          getIsEmpty();
          handleInputGetAllData();
          handleInput();
        "
        @paste="handlePaste"
        @keydown.backspace="getIsEmpty"
        @keypress.enter="handleEnter"
        @compositionstart="handleChineseInput"
        @compositionupdate="handleChineseInput"
        @compositionend="handleChineseInput"
      ></div>
      <div class="custom-edit-div-placeholder" v-show="placeholderIsShow">
        {{ props.placeholder }}
      </div>
    </div>
    <a-input
      v-else
      v-model="inputValue"
      :placeholder="placeholder"
      :readonly="props.readonly"
      :disabled="props.disabled"
    ></a-input>
    <template #content>
      <div class="custom-select-table" @mousedown="handleMousedown">
        <a-tabs v-model:active-key="selectActiveKey">
          <template #extra>
            <a-button
              v-if="selectActiveKey === 0"
              type="text"
              size="mini"
              style="margin-right: 16px"
              @click="handleOpenSetting"
            >
              <icon-settings />
              <span style="margin-left: 4px">{{
                $t('common.settingBtn')
              }}</span>
            </a-button>
          </template>
          <a-tab-pane
            v-for="(item, index) in selectTableList"
            :key="index"
            :title="item.title"
          >
            <trigger-tag-list
              :index="index"
              :loading="loading"
              :list="item.list"
              :mode="props.mode"
              @reachBottom="getSelectTableData"
              @select="handleSelect"
              @down="handleMousedown"
            >
            </trigger-tag-list>
          </a-tab-pane>
        </a-tabs>
      </div>
    </template>
  </c-trigger>

  <!-- 动态tooltip -->
  <el-tooltip
    popper-class="custom_tooltip"
    effect="gray"
    :visible="toolTipVisible"
    :virtual-ref="spanRef"
    placement="top"
    virtual-triggering
  >
    <template #content>
      <div class="tooltip-content">
        {{ $t('customArco.inputTriggerTag.tip') }}
      </div>
    </template>
  </el-tooltip>

  <!-- 设置弹窗 -->
  <c-modal
    v-model:visible="settingVisible"
    :title="settingTitle + $t('common.settingBtn')"
    width="1000px"
  >
    <template #footer>
      <a-button type="primary" @click="settingVisible = false">{{
        $t('common.close')
      }}</a-button>
    </template>
    <div class="setting_list">
      <div
        class="list_inner"
        :class="{
          list_inner_focus: settingFocusIndex === index,
        }"
        v-for="(item, index) in selectTableList[0].list"
        :key="item.id"
      >
        <div class="label default">
          {{ settingInputText(item) }}
        </div>
        <div class="hover">
          <c-trigger
            v-model:popup-visible="triggerListVisible[index]"
            :trigger="['click']"
            :click-to-close="false"
          >
            <a-input
              class="normal_suffix"
              :model-value="settingInputText(item)"
              @focus="settingFocusIndex = index"
              @blur="
                settingFocusIndex = -1;
                triggerListVisible[index] = false;
              "
            >
              <template #suffix>
                <icon-down :size="16" v-if="!triggerListVisible[index]" />
                <icon-up :size="16" v-else />
              </template>
            </a-input>
            <template #content>
              <div class="setting_select_table" @mousedown="handleMousedown">
                <trigger-tag-list
                  :index="index"
                  :loading="loading"
                  :list="tableData"
                  :mode="props.mode"
                  @reachBottom="getSelectTableData"
                  @select="handleSelectSettingInput($event, item, index)"
                >
                </trigger-tag-list>
              </div>
            </template>
          </c-trigger>
          <icon-delete
            class="deleteIcon"
            @click="handleDeleteFreqUsed(item, index)"
          />
        </div>
      </div>
      <div class="list_inner add_btn">
        <c-trigger
          v-model:popup-visible="triggerStVisible"
          :click-to-close="false"
          @popup-visible-change="handleVisibleChange"
        >
          <a-input
            v-model="filterName"
            :placeholder="
              $t('customArco.inputTriggerTag.placeH', {
                name: settingTitle,
              })
            "
            :bordered="false"
            @input="handleSearch(false)"
          >
            <template #prefix><icon-plus /></template>
          </a-input>
          <template #content>
            <div class="setting_select_table" @mousedown="handleMousedown">
              <trigger-tag-list
                :loading="loading"
                :list="tableData"
                :mode="props.mode"
                @reachBottom="getSelectTableData"
                @select="handleSelectSettingInput"
              >
              </trigger-tag-list>
            </div>
          </template>
        </c-trigger>
      </div>
    </div>
  </c-modal>
</template>

<script setup>
import triggerTagList from './triggerTagList.vue';
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue';
import _ from 'lodash';
import {
  isIncludeDiagnosisList,
  getUseDiagnosisList,
  saveUseDiagnosis,
  deleteUseDiagnosisById,
} from '@/api/writePrescription.js';

const { proxy } = getCurrentInstance();
const emit = defineEmits(['change', 'select']);
const props = defineProps({
  // trigger偏移量
  popupTranslate: {
    type: [Number, Array],
    default: [60, 0],
  },
  // 不存在于数据库的高亮显示
  highLight: {
    type: Boolean,
    default: false,
  },
  // 选择器模式
  selection: Boolean,
  // 模式 诊断: diagnosis; 辨证: discriminate; 治法: treatment;
  mode: {
    type: String,
    default: 'diagnosis',
    required: true,
  },
  // 数据绑定
  modelValue: {
    type: Array,
    default: [],
    required: true,
  },
  // 占位符
  placeholder: {
    type: String,
    default: '',
  },
  // 分割符
  splitSymbol: {
    type: String,
    default: '；',
  },
  // 格式化内容 (data: TagData) => string
  formatTag: {
    type: Function,
  },
  // 自定义字段
  fieldNames: {
    type: Object,
    default: {
      label: 'label',
      value: 'value',
    },
  },
  // 只读
  readonly: {
    type: Boolean,
    default: false,
  },
  // 禁用
  disabled: {
    type: Boolean,
    default: false,
  },
  // 错误
  error: {
    type: Boolean,
    default: false,
  },
});
// 选择器模式下使用的字段
const inputValue = ref('');
const { modelValue } = toRefs(props);
const computedValue = computed(() => props.modelValue || []);
const editDivRef = ref(null);
// 元素基础状态
const inputAttr = reactive({
  focus: false, // 是否聚焦
  chInput: false, // 是否处在中文输入
  input: false, // 是否正在输入
});
// 元素内部是否为空
const isEmpty = ref(true);
// toolTip
const toolTipVisible = ref(false);
const spanRef = ref(null);

watch(
  () => computedValue.value,
  (val) => {
    // 只在失焦或选择的时候触发
    if (inputAttr.input) return;
    clearContent();
    val.map((item) => {
      addElement(item[props.fieldNames.label], item[props.fieldNames.value]);
    });
    // getIsEmpty();
    // 聚焦状态添加进去不删除最后的分割符
    if (inputAttr.focus) return;
    deleteEndSplitSymbol();
  },
  { deep: true },
);
// 监听元素内容是否为空，全部清空把数据置空
watch(
  () => isEmpty.value,
  (val) => {
    if (val) clearContent();
  },
);

// 元素是否可编辑状态
const contenteditable = ref(true);
// 监听只读和禁用状态，动态改变可编辑状态
watch(
  [() => props.disabled, () => props.readonly],
  ([disabled, readonly]) => {
    contenteditable.value = !(disabled || readonly);
  },
  {
    immediate: true,
  },
);
// 占位符是否显示
const placeholderIsShow = computed(function () {
  return modelValue.value?.length === 0 && !inputAttr.chInput && isEmpty.value;
});
/**
 * 添加文本元素
 * @param {String} text 文本
 * @param {String} value 值
 */
const addElement = (text, value) => {
  const target = editDivRef.value;
  const span = document.createElement('span');
  span.innerText = text;
  span.setAttribute('data-value', value ?? '');
  // 允许高亮、并且value没有值，不属于标准
  if (props.highLight && !value && !inputAttr.focus) {
    span.className = 'high_light_text';
    span.onmouseenter = () => {
      if (span.className !== 'high_light_text') return;
      spanRef.value = span;
      toolTipVisible.value = true;
    };
    span.onmouseleave = () => {
      if (span.className !== 'high_light_text') return;
      toolTipVisible.value = false;
      spanRef.value = null;
    };
  } else {
    span.className = 'default_text';
  }
  target.appendChild(span);
  addSplitSymbol();
};
// 添加分割符
const addSplitSymbol = () => {
  const target = editDivRef.value;
  const i = document.createElement('i');
  i.setAttribute('contenteditable', 'false'); // 添加不可编辑属性
  i.innerText = props.splitSymbol;
  target.appendChild(i);
};
// 删除末尾的分割符
const deleteEndSplitSymbol = () => {
  const i = editDivRef.value.lastChild;
  if (i && i.nodeName === 'I') {
    editDivRef.value.removeChild(i);
  }
};
// 删除最后一个元素
const deleteEndText = () => {
  const lastChild = editDivRef.value.lastChild;
  editDivRef.value.removeChild(lastChild);
};
// 清空内部所有元素
const clearContent = () => {
  editDivRef.value.innerHTML = '';
};
// 获取全部内容
const getAllData = () => {
  const target = editDivRef.value;
  // 保存元素节点
  const childNodes = [];
  target.childNodes.forEach((ev) => {
    childNodes.push(ev);
  });
  // 存储所需格式节点
  const formatNodes = [];
  // 遍历格式化
  childNodes.forEach((ev) => {
    const text = ev.data || ev.innerText;
    const isEmpty = proxy.$filters.isAllSpace(text);
    if (text && !isEmpty && ev.nodeName !== 'I') {
      const label = text.trim();
      const value = ev.dataset?.value;
      // 判断是否存在
      const include = formatNodes.some(
        (node) =>
          node[props.fieldNames.value] == value &&
          node[props.fieldNames.label] === label,
      );
      if (!include) {
        formatNodes.push({
          [props.fieldNames.label]: label,
          [props.fieldNames.value]: value,
          highLight: !!!value,
        });
      }
    }
  });
  return formatNodes;
};
// 获取元素内是否有内容
const getIsEmpty = () => {
  isEmpty.value = !!!editDivRef.value.innerText.trim().length;
  if (isEmpty.value) emit('change', []);
};
// 聚焦到文本末尾
const focusEnd = ({ target }) => {
  const range = document.createRange();
  range.selectNodeContents(target);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
};
// 元素聚焦
const handleFocus = ({ target }) => {
  toolTipVisible.value = false;
  spanRef.value = null;
  // 元素末尾添加一个分割符
  const lastChild = target.lastChild;
  if (lastChild && lastChild.nodeName !== 'I') {
    addSplitSymbol();
  }
  // 将所有文本设置为default_text，不显示颜色
  target.childNodes.forEach((item) => {
    item.className = 'default_text';
  });
  // 设置为聚焦状态
  inputAttr.focus = true;
};
// 监听元素粘贴，清除样式
const handlePaste = (e) => {
  e.preventDefault();
  let text = '';
  const clp = (e.originalEvent || e).clipboardData;
  if (clp === undefined || clp === null) {
    text = window.clipboardData.getData('text') || '';
    if (text !== '') {
      if (window.getSelection) {
        var newNode = document.createElement('span');
        newNode.innerHTML = text;
        window.getSelection().getRangeAt(0).insertNode(newNode);
      } else {
        document.selection.createRange().pasteHTML(text);
      }
    }
  } else {
    text = clp.getData('text/plain') || '';
    if (text !== '') {
      document.execCommand('insertText', false, text);
    }
  }
};
// 元素输入的时候每次重新获取全部元素
const handleInputGetAllData = (ev) => {
  inputAttr.input = true;
  // 获取全部内容
  emit('change', getAllData());
};
// 搜索
const handleSearch = _.debounce((initStore = true) => {
  // 清空搜索条件
  page.value = 1;
  initStore && initStoreData();
  // 执行搜索
  initTableData(true).then((data) => {
    tableData.value = data;
  });
}, 300);
// 元素正在输入
const handleInput = () => {
  // 获取全部内容
  const data = getAllData();
  // 取最后一个元素
  const lastTextObj = data[data.length - 1];
  // 元素的文本
  const lastText = lastTextObj?.[props.fieldNames.label] ?? '';
  filterName.value = lastText;
  handleSearch();
};
// 元素失焦
const handleBlur = () => {
  inputAttr.focus = false;
  inputAttr.input = false;
  filterName.value = '';
  deleteEndSplitSymbol();
  // 存储所需格式节点
  const formatNodes = getAllData();
  getIsEmpty();
  if (isEmpty.value) {
    emit('change', formatNodes);
    return;
  }
  // 接口查询数据是否存在标准
  isIncludeDiagnosisList(
    formatNodes.map((ev) => {
      return {
        [props.fieldNames.label]: ev[props.fieldNames.label],
        [props.fieldNames.value]:
          ev[props.fieldNames.value] === 'null'
            ? null
            : ev[props.fieldNames.value],
        diseaseMedicalId: 3,
      };
    }),
  )
    .then((res) => {
      // 执行清空
      clearContent();
      let arr = [];
      res.data.map((item, index) => {
        // 过滤重复的
        if (
          !arr.some(
            (ev) =>
              ev[props.fieldNames.label] === item[props.fieldNames.label] &&
              ev[props.fieldNames.value] === item[props.fieldNames.value],
          )
        ) {
          arr.push({
            [props.fieldNames.label]: item[props.fieldNames.label],
            [props.fieldNames.value]: item[props.fieldNames.value],
          });
        }
      });
      emit('change', arr);
    })
    .catch(() => {
      emit('change', formatNodes);
    });
};
// 元素中文输入
const handleChineseInput = (ev) => {
  inputAttr.chInput = ev.isTrusted;
  getIsEmpty();
};
// 回车事件
const handleEnter = (e) => {
  e.preventDefault();
};

/** 下拉表格内容 */
import { frequentlyStore } from '@/store/modules/frequentlyUse.js';
import { getDiseaseList } from '@/api/disease.js';
import {
  getDiscriminateList,
  getTreatmentList,
} from '@/api/writePrescription.js';
const { readInfo, addInfo, replaceInfo } = frequentlyStore();
// 当前选中的tab页
const selectActiveKey = ref(0);
// 全部数据
const tableData = ref([]);
// 加载
const loading = ref(false);
// 分页
const page = ref(1);
const hasNextPages = ref(true);
const filterName = ref('');
const selectTableList = ref([
  {
    title: computed(() => proxy.$t('customArco.inputTriggerTag.common')),
    list: [],
  },
  {
    title: computed(() => proxy.$t('common.all')),
    list: computed(() => tableData.value),
  },
]);
// 定义接口
const API = ref(null);

// 获取常用数据
const initStoreData = async () => {
  const apiData = await getUseDiagnosisList(props.mode);
  const storeList = apiData.data.map((item) => {
    item.isApi = true;
    return item;
  });
  readInfo(props.mode).map((item) => {
    const isInclude = storeList.some((ev) => ev.id === item.id);
    if (!isInclude) {
      storeList.push(item);
    }
  });
  selectTableList.value[0].list = storeList.filter((ev) => {
    if (filterName.value) {
      return (
        ev[props.fieldNames.label].includes(filterName.value) ||
        ev.chooseName?.includes(filterName.value)
      );
    } else {
      return true;
    }
  });
};
// 触发器打开的时候重新获取常用数据
const handleVisibleChange = (visible) => {
  if (!visible) {
    // 关闭后初始化
    page.value = 1;
    tableData.value = [];
    hasNextPages.value = true;
    return;
  }
  initStoreData();
};
// 加载数据
const initTableData = (isReturn = false) => {
  return new Promise((resolve, reject) => {
    loading.value = true;
    API.value({
      [props.fieldNames.label]: props.selection
        ? inputValue.value
        : filterName.value,
      page: page.value,
      limit: 20,
      diseaseMedicalId: 3,
    })
      .then(({ data }) => {
        loading.value = false;
        hasNextPages.value = data.hasNextPages;
        hasNextPages.value && page.value++;
        if (!isReturn) {
          tableData.value.push(...data.items);
        }
        resolve(data.items);
      })
      .catch(() => {
        loading.value = false;
        reject();
      });
  });
};
// 滚动加载
const getSelectTableData = _.debounce(() => {
  // 没有下一页了
  if (!hasNextPages.value) return;
  initTableData();
}, 0);
// 选择
const handleSelect = (data) => {
  if (!props.selection) {
    // 记录到常用
    addInfo(props.mode, data);
    const arr = [data[props.fieldNames.label], data[props.fieldNames.value]];
    const flag = computedValue.value.find(
      (ev) =>
        ev[props.fieldNames.value] === arr[1] &&
        ev[props.fieldNames.label] === arr[0],
    );
    if (!flag) {
      // 如果有过滤name，则先删除最后一个文本元素再插入数据
      if (filterName.value) {
        filterName.value = '';
        deleteEndSplitSymbol();
        deleteEndText();
      }
      addElement(...arr);
      emit('change', getAllData());
    }
    nextTick(function () {
      focusEnd({ target: editDivRef.value });
    });
  } else {
    inputValue.value = '';
    emit('select', data);
  }
};
// 阻止鼠标按下默认事件，防止失焦
const handleMousedown = (e) => {
  e.preventDefault();
};

/* 设置模块(这里的设置常用模块是组合了本地缓存以及接口数据，isApi字段区分) */
import { Message, Modal } from '@arco-design/web-vue';
const settingVisible = ref(false);
const settingFocusIndex = ref(-1);
const settingTitle = computed(() => {
  return proxy.$t(`customArco.inputTriggerTag.${props.mode}`);
});
const triggerStVisible = ref(false);
const triggerListVisible = ref([]);
// 获取展示文本
const settingInputText = (item) => {
  let t1 = '';
  let t2 = '';
  let t3 = '';
  switch (props.mode) {
    case 'diagnosis':
      t1 = item.diseaseName;
      t2 = item.diseaseMedicalName;
      t3 = item.diseaseCode;
      break;
    case 'discriminate':
      t1 = item.discriminateName;
      t2 = item.chooseName;
      t3 = item.discriminateCode;
      break;
    case 'treatment':
      t1 = item.treatmentName;
      t2 = item.chooseName;
      t3 = item.treatmentCode;
      break;
  }
  return `${t1}（${t2 ? t2 + ' ' : ''}${t3}）`;
};
// 打开设置弹窗
const handleOpenSetting = () => {
  settingVisible.value = true;
};
// 删除
const handleDeleteFreqUsed = (item, index) => {
  Modal.warning({
    title: proxy.$t('customArco.inputTriggerTag.delModal.title'),
    content: proxy.$t('customArco.inputTriggerTag.delModal.content', {
      name: settingInputText(item),
    }),
    hideCancel: false,
    okText: proxy.$t('common.ok'),
    cancelText: proxy.$t('common.cancel'),
    modalStyle: {
      textAlign: 'center',
      wordBreak: 'break-all',
    },
    okButtonProps: {
      status: 'danger',
    },
    onBeforeOk: async (done) => {
      try {
        if (!item.isApi) {
          const storeList = _.cloneDeep(readInfo(props.mode));
          const storeIndex = storeList.findIndex((ev) => ev.id === item.id);
          storeIndex > -1 && storeList.splice(storeIndex, 1);
          selectTableList.value[0].list.splice(index, 1);
          replaceInfo(props.mode, _.cloneDeep(storeList));
        } else {
          const res = await deleteUseDiagnosisById(item.id);
          if (!res.data) {
            Message.error(proxy.$t('common.deleteFailed'));
            return false;
          }
          initStoreData();
        }
        done();
        Message.success(proxy.$t('common.deleteSuccess'));
      } catch {
        done(false);
      }
    },
  });
};
// 选择数据
const handleSelectSettingInput = async (e, crVal = {}, index) => {
  if (e.id === crVal?.id) {
    filterName.value = '';
    index >= 0 && (triggerListVisible.value[index] = false);
    nextTick(() => {
      settingFocusIndex.value = -1;
    });
    return Message.success(proxy.$t('common.saveSuccessed'));
  }
  const list = selectTableList.value[0].list;
  const isInclude = list.some((ev) => ev.id === e.id && ev.id !== crVal?.id);
  triggerStVisible.value = false;
  handleVisibleChange(false);
  if (isInclude) {
    filterName.value = '';
    index >= 0 && (triggerListVisible.value[index] = false);
    nextTick(() => {
      settingFocusIndex.value = -1;
    });
    return Message.warning(
      proxy.$t('customArco.inputTriggerTag.isInclude', {
        name: settingInputText(e),
      }),
    );
  }
  const data = {
    ...e,
    lastId: crVal.isApi && crVal.id,
    isApi: true,
  };
  try {
    const res = await saveUseDiagnosis(props.mode, data);
    if (!res.data) throw false;
    // 如果是缓存数据，替换缓存里的数据
    if (!crVal.isApi) {
      const storeList = _.cloneDeep(readInfo(props.mode));
      const storeIndex = storeList.findIndex((ev) => ev.id === crVal.id);
      storeIndex > -1 && storeList.splice(storeIndex, 1);
      replaceInfo(props.mode, _.cloneDeep(storeList));
    }
    initStoreData();
    Message.success(proxy.$t('common.saveSuccessed'));
  } catch (error) {
    index >= 0 && (triggerListVisible.value[index] = false);
  }
  filterName.value = '';
};

onBeforeMount(function () {
  switch (props.mode) {
    case 'diagnosis':
      API.value = getDiseaseList;
      break;
    case 'discriminate':
      API.value = getDiscriminateList;
      break;
    case 'treatment':
      API.value = getTreatmentList;
      break;
  }
});
</script>

<style lang="scss">
@import '@/assets/css/mixin.scss';
.custom-edit-div {
  @include FontSize_x14;
  width: 100%;
  position: relative;
  display: inline-block;
  border: 1px solid var(--color_neutral04);
  border-radius: var(--border-radius-small);
  transition:
    color 0.1s cubic-bezier(0, 0, 1, 1),
    border-color 0.1s cubic-bezier(0, 0, 1, 1),
    background-color 0.1s cubic-bezier(0, 0, 1, 1);
  i {
    font-style: normal;
  }
  // 聚焦状态、悬浮状态
  &.custom-edit-div-focus,
  &:hover {
    border-color: var(--color_brand);
    box-shadow: 0px 0px 4px rgba($color: #c0c4cc, $alpha: 0.8);
  }
  // 禁用状态
  &.custom-edit-div-disabled,
  &.custom-edit-div-disabled:hover {
    border-color: var(--color_neutral04);
    background-color: var(--color_neutral01);
    box-shadow: unset;
    color: var(--color_lightgray02);
    cursor: no-drop;
  }
  // 只读状态
  &.custom-edit-div-readonly,
  &.custom-edit-div-readonly:hover {
    border-color: var(--color_neutral04);
    background-color: var(--color_neutral01);
    box-shadow: unset;
    color: var(--color_black);
    cursor: no-drop;
  }
  // 错误状态
  &.custom-edit-div-error {
    border-color: rgb(var(--danger-6));
    background-color: var(--color_white);
    &:hover {
      border-color: rgb(var(--danger-6));
      box-shadow: 0px 0px 4px rgb(var(--danger-6), 0.8);
    }
  }
  .custom-edit-div-wrapper {
    outline: none;
    color: var(--color_black);
    position: relative;
    padding: 6px 12px;
    z-index: 2;
    min-height: 20px;
    .high_light_text {
      color: var(--color_warning);
      cursor: pointer;
      position: relative;
    }
  }
  .custom-edit-div-placeholder {
    color: var(--color_lightgray02);
    position: absolute;
    z-index: 1;
    top: 50%;
    transform: translateY(-50%);
    left: 12px;
  }
}
</style>
<style lang="scss" scoped>
@import '@/assets/css/mixin.scss';
/** 下拉表格内容 */
.custom-select-table {
  @include boxBordered;
  width: 700px;
  ::v-deep(.arco-tabs-content) {
    padding-top: 0;
  }
}
.setting_list {
  padding-top: 16px;
  padding-bottom: 8px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  .list_inner {
    border-right: var(--border_solid_gray);
    border-bottom: var(--border_solid_gray);
    position: relative;
    height: 44px;
    display: flex;
    align-items: center;
    color: var(--color_black);
    overflow: hidden;
    .hover {
      display: none;
    }
    .default {
      width: max-content;
      padding: 0 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &.list_inner_focus,
    &:hover {
      background-color: var(--color_hover);
      .default {
        display: none;
      }
      .hover {
        flex: 1;
        width: 0;
        display: flex;
        align-items: center;
        padding: 0 8px;
        .deleteIcon {
          margin-left: 8px;
          font-size: 18px;
          color: var(--color_lightgray01);
          cursor: pointer;
        }
      }
    }
    &:nth-child(2n - 1) {
      border-left: var(--border_solid_gray);
    }
    &:nth-child(1),
    &:nth-child(2) {
      border-top: var(--border_solid_gray);
    }
  }
  .list_inner.add_btn .arco-input-wrapper {
    border: none !important;
    box-shadow: none !important;
    background-color: transparent;
    ::v-deep(.arco-input-prefix) {
      background-color: transparent;
      border: none;
      margin-right: 0;
      padding-right: 8px;
      padding-left: 12px;
      color: var(--color_lightgray02);
      svg {
        font-size: 16px;
      }
    }
  }
}
.setting_select_table {
  width: 686px;
  border-radius: var(--border_radius_x4);
  background-color: var(--color_white);
  box-shadow: var(--box_shadow);
}
</style>
